import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'remixicon/fonts/remixicon.css';

const selectStyles = {
    control: (base) => ({
        ...base,
        height: '50px',
        minHeight: '50px',
        borderRadius: '0.375rem',
        borderColor: '#d9dee3',
        fontSize: '0.9375rem',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b2b5c0' }
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '14px 14px 0 14px',
        height: '48px',
        overflow: 'hidden'
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#a1acb8'
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#566a7f'
    }),
    input: (base) => ({
        ...base,
        margin: '0',
        padding: '0'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '48px'
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        fontSize: '0.9375rem'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#0b5cab' : state.isFocused ? '#f5f5f9' : 'white',
        color: state.isSelected ? 'white' : '#566a7f',
        cursor: 'pointer',
    })
};

const SearchableSelect = ({ label, options, value, onChange, isDisabled, placeholder, selectStyles: ignoredSelectStyles }) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value !== null && value !== undefined && value !== '';
    const shouldFloat = isFocused || hasValue;

    return (
        <div className="position-relative" style={{ minHeight: '50px', height: '50px' }}>
            <Select
                options={options}
                value={value}
                onChange={onChange}
                isDisabled={isDisabled}
                placeholder={isFocused ? placeholder : ""}
                isSearchable
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                styles={selectStyles}
            />
            <label
                style={{
                    position: 'absolute',
                    left: '10px',
                    top: shouldFloat ? '-8px' : '14px',
                    fontSize: shouldFloat ? '0.75rem' : '0.9375rem',
                    color: shouldFloat ? '#0b5cab' : '#8592a3',
                    pointerEvents: 'none',
                    transition: 'all 0.2s ease',
                    zIndex: 4,
                    backgroundColor: shouldFloat ? 'white' : 'transparent',
                    padding: '0 6px',
                    lineHeight: '1'
                }}
            >
                {label}
            </label>
        </div>
    );
};

const riskOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Moderate', label: 'Moderate' },
    { value: 'High', label: 'High' }
];

const relationOptions = [
    { value: 'Father', label: 'Father' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Brother', label: 'Brother' },
    { value: 'Sister', label: 'Sister' },
    { value: 'Spouse', label: 'Spouse' },
    { value: 'Other', label: 'Other' }
];

const relationshipOptions = [
    { value: 'Father', label: 'Father' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Son', label: 'Son' },
    { value: 'Daughter', label: 'Daughter' },
    { value: 'Other', label: 'Other' }
];

const serviceOptions = [
    { value: 'Residential Program', label: 'Residential Program' },
    { value: 'Day Rehab Program', label: 'Day Rehab Program' },
    { value: 'Home Visitation', label: 'Home Visitation' },
    { value: 'Therapy Services', label: 'Therapy Services' }
];

const NursingOPDAssessmentForm = ({ initialData, onSubmit, isLoading, buttonText, patients = [], initialPatientId }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        // Section 1: Identification
        patient_id: '',
        admitted_to: '',
        consent_taken_from: 'Both',
        // Section 2: Escort
        escort_reason: '',
        guardian_name: '',
        relation: '',
        // Section 3: Staff
        nursing_name: '',
        nursing_supervisor: '',
        // Section 4: Assessment
        mode_of_transportation: 'Self',
        // Section 5: Vitals
        blood_pressure: '',
        respiratory_rate: '',
        bmi: '',
        pulse: '',
        weight: '',
        covid_status: '',
        temperature: '',
        height: '',
        rbs: '',
        ecg_done: 'No',
        ecg_no_reason: '',
        physical_assessment: '',
        skin_assessment: '',
        medical_assessment: '',
        referral_ziauddin: '',
        medical_condition: '',
        ziauddin_advise_brief: '',
        // Section 6: Risks
        roh_risk: '',
        ros_risk: '',
        rov_risk: '',
        roe_risk: '',
        rof_risk: '',
        rovu_risk: '',
        // Section 7: Labs (Repeater)
        labs_investigations: [{ id: Date.now(), test_name: '' }],
        // Section 8: Restrain
        restrain_order_by: '',
        restrain_physical: '',
        restrain_chemical: '',
        restrain_type: 'Both',
        info_given: {
            admission_process: false,
            inform_restrain: false,
            inform_ecg: false,
            inform_injection: false,
            uniform_change: false,
            introduce_staff: false,
            belongings_not_allowed: false,
            provided_by_karwan: false,
            inform_food: false,
            inform_canteen: false
        },
        // Section 9: Nursing Plan (Repeater)
        nursing_plans: [{ id: Date.now(), plan: '' }],
        review_supervisor: '',
        review_name: '',
        review_signature: '',
        review_date: '',
        review_time: '',
        // Section 10: Valuables
        valuables_description: '',
        valuables_quantity: '',
        valuables_condition: '',
        handed_over_clothes: 'No',
        handed_over_wallet: 'No',
        handed_over_jewelry: 'No',
        handed_over_purse: 'No',
        handed_over_other: '',
        // Section 11: Final
        family_signature: '',
        staff_signature: '',
        authenticator_relationship: '',
        final_date: '',
        receiving_staff_name: '',
        receiving_staff_signature: '',
        is_referred: 'No',
        referral_service: ''
    });

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        if (initialPatientId) {
            setFormData(prev => ({ ...prev, patient_id: initialPatientId }));
        }
    }, [initialPatientId]);

    const patientOptions = patients.map(p => ({
        value: p.id,
        label: `${p.first_name || ''} ${p.last_name || ''} - ${p.cnic || 'No CNIC'}`
    }));

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name.startsWith('info_')) {
            const field = name.replace('info_', '');
            setFormData(prev => ({
                ...prev,
                info_given: { ...prev.info_given, [field]: checked }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRepeaterChange = (section, id, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: prev[section].map(item => item.id === id ? { ...item, [section === 'labs_investigations' ? 'test_name' : 'plan']: value } : item)
        }));
    };

    const addRow = (section) => {
        const newRow = section === 'labs_investigations' ? { id: Date.now(), test_name: '' } : { id: Date.now(), plan: '' };
        setFormData(prev => ({ ...prev, [section]: [...prev[section], newRow] }));
    };

    const removeRow = (section, id) => {
        if (formData[section].length > 1) {
            setFormData(prev => ({ ...prev, [section]: prev[section].filter(item => item.id !== id) }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="accordion" id="nursingOPDAccordion">
                {/* 1. Patient & Staff Identification */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#secIdentification">
                            1. Patient & Staff Identification
                        </button>
                    </h2>
                    <div id="secIdentification" className="accordion-collapse collapse show">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <SearchableSelect
                                        label="Select Patient"
                                        options={patientOptions}
                                        value={patientOptions.find(opt => Number(opt.value) === Number(formData.patient_id)) || null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, patient_id: opt ? opt.value : "" }))}
                                        isDisabled={!!initialData}
                                        placeholder="Search and Select Patient..."
                                        selectStyles={selectStyles}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="admitted_to" value={formData.admitted_to} onChange={handleInputChange} placeholder="Admitted To" />
                                        <label>Admitted To</label>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <small className="d-block mb-1 fw-semibold">Consent Taken From:</small>
                                    <div className="d-flex gap-3 mt-2 justify-content-center">
                                        {['Patient', 'Family', 'Both'].map(val => (
                                            <div className="form-check" key={val}>
                                                <input className="form-check-input" type="radio" name="consent_taken_from" value={val} checked={formData.consent_taken_from === val} onChange={handleInputChange} />
                                                <label className="form-check-label">{val}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Escort Details */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secEscort">
                            2. Escort Details
                        </button>
                    </h2>
                    <div id="secEscort" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <p className="text-primary fw-bold small">If Patient does not come for treatment and admission</p>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="escort_reason" value={formData.escort_reason} onChange={handleInputChange} placeholder="Reason" />
                                        <label>Reason for Escort</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="guardian_name" value={formData.guardian_name} onChange={handleInputChange} placeholder="Guardian Name" />
                                        <label>Guardian Name</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <SearchableSelect
                                        label="Relation"
                                        options={relationOptions}
                                        value={relationOptions.find(opt => opt.value === formData.relation) || null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, relation: opt ? opt.value : "" }))}
                                        placeholder="Select Relation"
                                        selectStyles={selectStyles}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Attending Staff */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secStaff">
                            3. Attending Staff
                        </button>
                    </h2>
                    <div id="secStaff" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="nursing_name" value={formData.nursing_name} onChange={handleInputChange} placeholder="Nursing" />
                                        <label>Nursing</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="nursing_supervisor" value={formData.nursing_supervisor} onChange={handleInputChange} placeholder="Supervisor" />
                                        <label>Nursing Supervisor/OPD Co-ordinator</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Nursing Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secAssessment">
                            4. Nursing Assessment
                        </button>
                    </h2>
                    <div id="secAssessment" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <small className="d-block mb-1 fw-semibold">Mode of Transportation:</small>
                            <div className="d-flex gap-3 mt-2 justify-content-center">
                                {['Wheelchair', 'Ambulance', 'Self'].map(val => (
                                    <div className="form-check" key={val}>
                                        <input className="form-check-input" type="radio" name="mode_of_transportation" value={val} checked={formData.mode_of_transportation === val} onChange={handleInputChange} />
                                        <label className="form-check-label">{val}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. Vitals */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secVitals">
                            5. Vitals
                        </button>
                    </h2>
                    <div id="secVitals" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3 mb-4">
                                {['blood_pressure', 'respiratory_rate', 'bmi', 'pulse', 'weight', 'covid_status', 'temperature', 'height', 'rbs'].map(f => (
                                    <div className="col-md-4" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name={f} value={formData[f]} onChange={handleInputChange} placeholder={f.replace('_', ' ').toUpperCase()} />
                                            <label>{f.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-md-4">
                                    <small className="d-block mb-1 fw-semibold">ECG Done?</small>
                                    <div className="d-flex gap-3 mt-1 justify-content-center">
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="ecg_done" value="Yes" checked={formData.ecg_done === 'Yes'} onChange={handleInputChange} />
                                            <label className="form-check-label">Yes</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="ecg_done" value="No" checked={formData.ecg_done === 'No'} onChange={handleInputChange} />
                                            <label className="form-check-label">No</label>
                                        </div>
                                    </div>
                                </div>
                                {formData.ecg_done === 'No' && (
                                    <div className="col-md-8">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="ecg_no_reason" value={formData.ecg_no_reason} onChange={handleInputChange} placeholder="Reason" />
                                            <label>Reason for no ECG</label>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="row g-3">
                                {['physical_assessment', 'skin_assessment', 'medical_assessment', 'referral_ziauddin', 'medical_condition', 'ziauddin_advise_brief'].map(f => (
                                    <div className="col-md-6" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <textarea className="form-control h-px-100" name={f} value={formData[f]} onChange={handleInputChange}></textarea>
                                            <label>{f.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 6. Risk Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secRisks">
                            6. OPD Assessment Form for Admission Patient Information (Risks)
                        </button>
                    </h2>
                    <div id="secRisks" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {[
                                    { k: 'roh_risk', l: 'ROH (Risk of Homicidal)' },
                                    { k: 'ros_risk', l: 'ROS (Risk of Suicide)' },
                                    { k: 'rov_risk', l: 'ROV (Risk of Violence)' },
                                    { k: 'roe_risk', l: 'ROE (Risk of Escape)' },
                                    { k: 'rof_risk', l: 'ROF (Risk of Falls)' },
                                    { k: 'rovu_risk', l: 'ROVU (Risk of Vulnerability)' }
                                ].map(item => (
                                    <div className="col-md-4" key={item.k}>
                                        <SearchableSelect
                                            label={item.l}
                                            options={riskOptions}
                                            value={riskOptions.find(opt => opt.value === formData[item.k]) || null}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, [item.k]: opt ? opt.value : "" }))}
                                            placeholder="Select Risk"
                                            selectStyles={selectStyles}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 7. Labs Investigation */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secLabs">
                            7. Labs Investigation
                        </button>
                    </h2>
                    <div id="secLabs" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            {formData.labs_investigations.map((row, index) => (
                                <div className="row g-2 mb-2 align-items-center" key={row.id}>
                                    <div className="col">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" value={row.test_name} onChange={(e) => handleRepeaterChange('labs_investigations', row.id, e.target.value)} placeholder="Lab Test" />
                                            <label>Lab Test {index + 1}</label>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-outline-danger btn-icon" onClick={() => removeRow('labs_investigations', row.id)}>
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={() => addRow('labs_investigations')}>
                                <i className="ri-add-line me-1"></i> Add Lab Test
                            </button>
                        </div>
                    </div>
                </div>

                {/* 8. Restrain Patient */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secRestrain">
                            8. Restrain Patient
                        </button>
                    </h2>
                    <div id="secRestrain" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3 mb-4">
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="restrain_order_by" value={formData.restrain_order_by} onChange={handleInputChange} placeholder="Order By" />
                                        <label>Order By</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="restrain_physical" value={formData.restrain_physical} onChange={handleInputChange} placeholder="Physical" />
                                        <label>Physical only</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="restrain_chemical" value={formData.restrain_chemical} onChange={handleInputChange} placeholder="Chemical" />
                                        <label>Chemical only</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <small className="d-block mb-1 fw-semibold">Type:</small>
                                    <div className="d-flex gap-2 justify-content-center">
                                        {['Physical', 'Chemical', 'Both'].map(val => (
                                            <div className="form-check" key={val}>
                                                <input className="form-check-input" type="radio" name="restrain_type" value={val} checked={formData.restrain_type === val} onChange={handleInputChange} />
                                                <label className="form-check-label">{val}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-light p-3 rounded">
                                <p className="fw-bold mb-3 border-bottom pb-2">Information given to patient/guardian:</p>
                                <div className="row">
                                    {[
                                        { k: 'admission_process', l: 'Admission process and waiting time' },
                                        { k: 'inform_restrain', l: 'Inform about restrain' },
                                        { k: 'inform_ecg', l: 'Inform about ECG' },
                                        { k: 'inform_injection', l: 'Inform about any injection given' },
                                        { k: 'uniform_change', l: 'Changing of uniform in assessment room' },
                                        { k: 'introduce_staff', l: 'Introduce ward staff' },
                                        { k: 'belongings_not_allowed', l: 'Inform about patient belongings not allowed' },
                                        { k: 'provided_by_karwan', l: 'Everything needed provided by Karwan-e-Hayat' },
                                        { k: 'inform_food', l: 'Inform about food (private/welfare)' },
                                        { k: 'inform_canteen', l: 'Inform about canteen account' }
                                    ].map(item => (
                                        <div className="col-md-6 mb-2" key={item.k}>
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" name={`info_${item.k}`} checked={formData.info_given[item.k]} onChange={handleInputChange} id={`chk_${item.k}`} />
                                                <label className="form-check-label" htmlFor={`chk_${item.k}`}>{item.l}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 9. Nursing Plan */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secPlan">
                            9. Nursing Plan
                        </button>
                    </h2>
                    <div id="secPlan" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <p className="fw-bold text-primary mb-3">Nursing Plan Details</p>
                            {formData.nursing_plans.map((row, index) => (
                                <div className="row g-2 mb-2 align-items-center" key={row.id}>
                                    <div className="col">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" value={row.plan} onChange={(e) => handleRepeaterChange('nursing_plans', row.id, e.target.value)} placeholder="Plan" />
                                            <label>Nursing Plan {index + 1}</label>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-outline-danger btn-icon" onClick={() => removeRow('nursing_plans', row.id)}>
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="btn btn-sm btn-outline-primary mt-2 mb-4" onClick={() => addRow('nursing_plans')}>
                                <i className="ri-add-line me-1"></i> Add Plan Row
                            </button>

                            <div className="bg-light p-3 rounded">
                                <p className="fw-bold mb-3 border-bottom pb-2">Review by:</p>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="review_supervisor" value={formData.review_supervisor} onChange={handleInputChange} placeholder="Supervisor" />
                                            <label>Nursing Supervisor/OPD Co-ordinator</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="review_name" value={formData.review_name} onChange={handleInputChange} placeholder="Name" />
                                            <label>Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="review_signature" value={formData.review_signature} onChange={handleInputChange} placeholder="Signature" />
                                            <label>Signature</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                            <input type="date" className="form-control" name="review_date" value={formData.review_date} onChange={handleInputChange} />
                                            <label>Date</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                            <input type="time" className="form-control" name="review_time" value={formData.review_time} onChange={handleInputChange} />
                                            <label>Time</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 10. Patient Valuables and Belongings */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secValuables">
                            10. Patient Valuables and Belongings
                        </button>
                    </h2>
                    <div id="secValuables" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <p className="fw-bold border-bottom pb-1">Valuables:</p>
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="valuables_description" value={formData.valuables_description} onChange={handleInputChange} placeholder="Description" />
                                        <label>Description of items</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="number" className="form-control" name="valuables_quantity" value={formData.valuables_quantity} onChange={handleInputChange} placeholder="Qty" />
                                        <label>Quantity</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="valuables_condition" value={formData.valuables_condition} onChange={handleInputChange} placeholder="Condition" />
                                        <label>Condition</label>
                                    </div>
                                </div>
                            </div>

                            <p className="fw-bold border-bottom pb-1">Belongings Handed Over:</p>
                            <div className="row g-3">
                                {[
                                    { k: 'handed_over_clothes', l: 'Clothes' },
                                    { k: 'handed_over_wallet', l: 'Wallet/Violet' },
                                    { k: 'handed_over_jewelry', l: 'Jewelry' },
                                    { k: 'handed_over_purse', l: 'Purse' }
                                ].map(item => (
                                    <div className="col-md-3" key={item.k}>
                                        <small className="d-block mb-1">{item.l}</small>
                                        <div className="d-flex gap-3 justify-content-center">
                                            <div className="form-check">
                                                <input className="form-check-input" type="radio" name={item.k} value="Yes" checked={formData[item.k] === 'Yes'} onChange={handleInputChange} />
                                                <label className="form-check-label">Yes</label>
                                            </div>
                                            <div className="form-check">
                                                <input className="form-check-input" type="radio" name={item.k} value="No" checked={formData[item.k] === 'No'} onChange={handleInputChange} />
                                                <label className="form-check-label">No</label>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-md-12 mt-3">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="handed_over_other" value={formData.handed_over_other} onChange={handleInputChange} placeholder="Other specify" />
                                        <label>Other (please specify)</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 11. Final Authentication */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secFinal">
                            11. Final Authentication
                        </button>
                    </h2>
                    <div id="secFinal" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="family_signature" value={formData.family_signature} onChange={handleInputChange} placeholder="Signature" />
                                        <label>Family Member/Attendant Signature</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="staff_signature" value={formData.staff_signature} onChange={handleInputChange} placeholder="Signature" />
                                        <label>Nursing Staff Signature</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <SearchableSelect
                                        label="Relationship to Patient"
                                        options={relationshipOptions}
                                        value={relationshipOptions.find(opt => opt.value === formData.authenticator_relationship) || null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, authenticator_relationship: opt ? opt.value : "" }))}
                                        placeholder="Select Relation"
                                        selectStyles={selectStyles}
                                    />
                                </div>
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" name="final_date" value={formData.final_date} onChange={handleInputChange} />
                                        <label>Date</label>
                                    </div>
                                </div>

                                <div className="col-md-12 mt-3 p-3 bg-light rounded shadow-sm border">
                                    <div className="d-flex flex-wrap align-items-center gap-4">
                                        <div>
                                            <label className="fw-bold me-3">Do you want to refer?</label>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="is_referred" id="referYes" value="Yes" checked={formData.is_referred === 'Yes'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="referYes">Yes</label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="is_referred" id="referNo" value="No" checked={formData.is_referred === 'No'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="referNo">No</label>
                                            </div>
                                        </div>

                                        {formData.is_referred === 'Yes' && (
                                            <div className="flex-grow-1" style={{ minWidth: '250px' }}>
                                                <SearchableSelect
                                                    label="Select Service"
                                                    options={serviceOptions}
                                                    value={serviceOptions.find(opt => opt.value === formData.referral_service) || null}
                                                    onChange={(opt) => setFormData(prev => ({ ...prev, referral_service: opt ? opt.value : "" }))}
                                                    placeholder="Select Service"
                                                    selectStyles={selectStyles}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-12 mt-4">
                                    <p className="fw-bold border-bottom pb-1">Ward Staff Receiving:</p>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                                <input type="text" className="form-control" name="receiving_staff_name" value={formData.receiving_staff_name} onChange={handleInputChange} placeholder="Staff Name" />
                                                <label>Staff Name</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                                <input type="text" className="form-control" name="receiving_staff_signature" value={formData.receiving_staff_signature} onChange={handleInputChange} placeholder="Signature" />
                                                <label>Signature</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-5 pt-3 border-top d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/nursing-opd-assessments')}>
                    <i className="ri-arrow-left-line me-1"></i>Back
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="ri-save-line me-1"></i>{buttonText || 'Save Assessment'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default NursingOPDAssessmentForm;
